import React, { useState, useEffect } from 'react';
import { getAdminProducts, createProduct, updateProduct, deleteProduct, getProduct } from '../../services/api';
import { formatCurrency, formatCurrencyShort } from '../../utils/currency';
import AddProductModal from '../../components/AddProductModal';
import ViewProductModal from '../../components/ViewProductModal';
import DeleteProductModal from '../../components/DeleteProductModal';
import { useToast } from '../../contexts/ToastContext';

function Products() {
  const { showSuccess, showError } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [stockFilter, setStockFilter] = useState('all'); // all, in_stock, low_stock, out_of_stock
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch all products including inactive ones for admin view
        const token = localStorage.getItem('auth_token');
        if (!token) {
          showError('Authentication required. Please log in again.');
          setLoading(false);
          return;
        }
        const data = await getAdminProducts(token);
        
        // If we get a single product or an array, handle both cases
        const productsList = Array.isArray(data) ? data : [data];
        setProducts(productsList);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        showError(error.message || 'Failed to load products. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [showError]);

  const handleAddProduct = async (productData) => {
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        const error = new Error('Authentication required. Please log in again.');
        showError(error.message);
        setSubmitLoading(false);
        throw error;
      }
      await createProduct(productData, token);
      // Refresh products list using admin endpoint
      const data = await getAdminProducts(token);
      const productsList = Array.isArray(data) ? data : [data];
      setProducts(productsList);
      // Don't close modal - let user add more products
    } catch (error) {
      showError(error.message || 'Failed to create product. Please try again.');
      setSubmitLoading(false);
      throw error;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleViewProduct = async (productId) => {
    try {
      const product = await getProduct(productId);
      setSelectedProduct(product);
      setIsViewModalOpen(true);
    } catch (error) {
      showError(error.message || 'Failed to load product details.');
    }
  };

  const handleEditProduct = async (productId) => {
    try {
      const product = await getProduct(productId);
      setSelectedProduct(product);
      setIsEditModalOpen(true);
    } catch (error) {
      showError(error.message || 'Failed to load product for editing.');
    }
  };

  const handleUpdateProduct = async (productData) => {
    if (!selectedProduct) return;
    
    console.log('🔍 Products.jsx - handleUpdateProduct received:', productData);
    console.log('🔍 Products.jsx - cost_price:', productData.cost_price, 'Type:', typeof productData.cost_price);
    
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        const error = new Error('Authentication required. Please log in again.');
        showError(error.message);
        setSubmitLoading(false);
        throw error;
      }
      await updateProduct(selectedProduct.id, productData, token);
      // Refresh products list using admin endpoint
      const data = await getAdminProducts(token);
      const productsList = Array.isArray(data) ? data : [data];
      setProducts(productsList);
      setIsEditModalOpen(false);
      setSelectedProduct(null);
      showSuccess('Product updated successfully!');
    } catch (error) {
      showError(error.message || 'Failed to update product. Please try again.');
      setSubmitLoading(false);
      throw error;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        setSubmitLoading(false);
        return;
      }
      await deleteProduct(productId, token);
      // Refresh products list using admin endpoint
      const data = await getAdminProducts(token);
      const productsList = Array.isArray(data) ? data : [data];
      setProducts(productsList);
      setIsDeleteModalOpen(false);
      setSelectedProduct(null);
      showSuccess('Product deleted successfully!');
    } catch (error) {
      showError(error.message || 'Failed to delete product. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const openDeleteModal = (product) => {
    setSelectedProduct(product);
    setIsDeleteModalOpen(true);
  };

  const handleToggleProductStatus = async (product) => {
    if (!product || !product.id) return;
    
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        setSubmitLoading(false);
        return;
      }
      
      // Toggle the is_active status
      const newStatus = !(product.is_active !== false);
      await updateProduct(product.id, { is_active: newStatus }, token);
      
      // Refresh products list using admin endpoint
      const data = await getAdminProducts(token);
      const productsList = Array.isArray(data) ? data : [data];
      setProducts(productsList);
      
      showSuccess(`Product ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      showError(error.message || 'Failed to update product status. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Helper function to calculate total stock across all variants
  const calculateTotalStock = (product) => {
    const hasColors = product.colors && Array.isArray(product.colors) && product.colors.length > 0;
    const hasSizes = product.sizes && Array.isArray(product.sizes) && product.sizes.length > 0;
    
    // If product has both colors and sizes, sum variant_stock
    if (hasColors && hasSizes && product.variant_stock && typeof product.variant_stock === 'object') {
      const totalVariantStock = Object.values(product.variant_stock).reduce((sum, stockQty) => {
        const stockNum = parseInt(stockQty) || 0;
        return sum + stockNum;
      }, 0);
      if (totalVariantStock > 0) {
        return totalVariantStock;
      }
    }
    
    // If product has colors (with or without sizes), sum color_stock
    if (hasColors && product.color_stock && typeof product.color_stock === 'object') {
      const totalColorStock = Object.values(product.color_stock).reduce((sum, stockQty) => {
        const stockNum = parseInt(stockQty) || 0;
        return sum + stockNum;
      }, 0);
      if (totalColorStock > 0) {
        return totalColorStock;
      }
    }
    
    // Fallback to general stock
    return product.stock || 0;
  };

  // Calculate total stock value at selling price
  const totalStockValueAtSelling = products.reduce((sum, product) => {
    const stock = calculateTotalStock(product);
    const price = parseFloat(product.price || 0);
    return sum + (stock * price);
  }, 0);

  // Calculate total stock value at cost price
  const totalStockValueAtCost = products.reduce((sum, product) => {
    const stock = calculateTotalStock(product);
    const costPrice = parseFloat(product.cost_price || product.original_price || product.price || 0);
    return sum + (stock * costPrice);
  }, 0);

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (product.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && product.is_active !== false) ||
                         (statusFilter === 'inactive' && product.is_active === false);
    
    const stock = calculateTotalStock(product);
    const matchesStock = stockFilter === 'all' ||
                        (stockFilter === 'in_stock' && stock > 10) ||
                        (stockFilter === 'low_stock' && stock > 0 && stock <= 10) ||
                        (stockFilter === 'out_of_stock' && stock === 0);
    
    return matchesSearch && matchesStatus && matchesStock;
  });

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div className="mb-4 md:mb-6 lg:mb-8">
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="w-1.5 h-8 md:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight drop-shadow-sm">Products</h1>
            <p className="text-xs md:text-sm text-gray-600 font-medium mt-1">Manage your product catalog</p>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3 lg:gap-4 mb-4 md:mb-6">
          {/* Search Input */}
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 md:pl-12 pr-3 md:pr-4 py-2 md:py-3 bg-white border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 md:px-4 py-2 md:py-3 bg-white border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-semibold cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Stock Filter */}
          <select
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
            className="px-3 md:px-4 py-2 md:py-3 bg-white border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-semibold cursor-pointer"
          >
            <option value="all">All Stock</option>
            <option value="in_stock">In Stock</option>
            <option value="low_stock">Low Stock</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>

          {/* Add Product Button */}
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="group relative overflow-hidden bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl hover:shadow-2xl hover:shadow-cyan-500/40 transition-all duration-300 transform hover:-translate-y-0.5 font-bold text-xs md:text-sm flex items-center gap-1.5 md:gap-2 whitespace-nowrap border-2 border-cyan-400/30"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-white/10 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/50 via-blue-400/50 to-indigo-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
            <svg className="w-5 h-5 relative z-10 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            <span className="relative z-10">Add Product</span>
          </button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 lg:gap-4 mb-4 md:mb-6">
          {/* Total Products */}
          <div className="relative bg-gradient-to-br from-blue-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-blue-300/50 p-2 md:p-3 lg:p-3.5 shadow-lg md:shadow-xl shadow-blue-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-blue-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 opacity-70"></div>
            {/* Decorative corner gradient */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-1.5 md:mb-2 lg:mb-2.5">
                <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Total Stock</span>
                <div className="relative w-7 h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg shadow-blue-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-xl md:group-hover:shadow-2xl transition-all duration-500">
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md md:rounded-lg opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
                </div>
              </div>
              <p className="text-lg md:text-xl lg:text-2xl font-black text-blue-700 mb-0.5 md:mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{products.length}</p>
              <div className="w-8 md:w-10 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full opacity-60 mt-1 md:mt-2 group-hover:w-full transition-all duration-500"></div>
            </div>
          </div>

          {/* Stock Value at Selling */}
          <div className="relative bg-gradient-to-br from-purple-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-purple-300/50 p-2 md:p-3 lg:p-3.5 shadow-lg md:shadow-xl shadow-purple-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-purple-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-600 opacity-70"></div>
            {/* Decorative corner gradient */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-1.5 md:mb-2 lg:mb-2.5">
                <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Stock Value at Selling</span>
                <div className="relative w-7 h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg shadow-purple-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-xl md:group-hover:shadow-2xl transition-all duration-500">
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-md md:rounded-lg opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
                </div>
              </div>
              <p className="text-base md:text-lg lg:text-xl font-black text-purple-700 mb-0.5 md:mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{formatCurrencyShort(totalStockValueAtSelling)}</p>
              <div className="w-8 md:w-10 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full opacity-60 mt-1 md:mt-2 group-hover:w-full transition-all duration-500"></div>
            </div>
          </div>

          {/* Stock Value at Cost */}
          <div className="relative bg-gradient-to-br from-emerald-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-emerald-300/50 p-2 md:p-3 lg:p-3.5 shadow-lg md:shadow-xl shadow-emerald-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-emerald-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 opacity-70"></div>
            {/* Decorative corner gradient */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-1.5 md:mb-2 lg:mb-2.5">
                <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Stock Value at Cost</span>
                <div className="relative w-7 h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg shadow-emerald-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-xl md:group-hover:shadow-2xl transition-all duration-500">
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-md md:rounded-lg opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
                </div>
              </div>
              <p className="text-base md:text-lg lg:text-xl font-black text-emerald-700 mb-0.5 md:mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{formatCurrencyShort(totalStockValueAtCost)}</p>
              <div className="w-8 md:w-10 h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full opacity-60 mt-1 md:mt-2 group-hover:w-full transition-all duration-500"></div>
            </div>
          </div>

          {/* Low Stock */}
          <div className="relative bg-gradient-to-br from-orange-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-orange-300/50 p-2 md:p-3 lg:p-3.5 shadow-lg md:shadow-xl shadow-orange-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-orange-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 opacity-70"></div>
            {/* Decorative corner gradient */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-orange-400 to-red-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-1.5 md:mb-2 lg:mb-2.5">
                <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Low Stock</span>
                <div className="relative w-7 h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg shadow-orange-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-xl md:group-hover:shadow-2xl transition-all duration-500">
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-600 rounded-md md:rounded-lg opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
                </div>
              </div>
              <p className="text-lg md:text-xl lg:text-2xl font-black text-orange-700 mb-0.5 md:mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{products.filter(p => {
                const stock = calculateTotalStock(p);
                return stock > 0 && stock <= 10;
              }).length}</p>
              <div className="w-8 md:w-10 h-0.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full opacity-60 mt-1 md:mt-2 group-hover:w-full transition-all duration-500"></div>
            </div>
          </div>

          {/* Out of Stock */}
          <div className="relative bg-gradient-to-br from-red-50/80 via-white to-white rounded-lg md:rounded-xl lg:rounded-2xl border-2 border-red-300/50 p-2 md:p-3 lg:p-3.5 shadow-lg md:shadow-xl shadow-red-200/50 hover:shadow-xl md:hover:shadow-2xl hover:shadow-red-300/50 hover:-translate-y-0.5 md:hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-red-600 opacity-70"></div>
            {/* Decorative corner gradient */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-red-400 to-red-500 rounded-full opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-1.5 md:mb-2 lg:mb-2.5">
                <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900 uppercase tracking-wider">Out of Stock</span>
                <div className="relative w-7 h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-md md:rounded-lg flex items-center justify-center text-white shadow-md md:shadow-lg shadow-red-300/50 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-xl md:group-hover:shadow-2xl transition-all duration-500">
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-600 rounded-md md:rounded-lg opacity-50 blur-xl -z-10 group-hover:opacity-75 transition-opacity duration-500"></div>
                </div>
              </div>
              <p className="text-lg md:text-xl lg:text-2xl font-black text-red-700 mb-0.5 md:mb-1 leading-none group-hover:scale-105 transition-transform duration-300">{products.filter(p => calculateTotalStock(p) === 0).length}</p>
              <div className="w-8 md:w-10 h-0.5 bg-gradient-to-r from-red-500 to-red-600 rounded-full opacity-60 mt-1 md:mt-2 group-hover:w-full transition-all duration-500"></div>
            </div>
          </div>

        </div>
      </div>

      {/* Products Table */}
      <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white rounded-xl md:rounded-2xl border-2 border-gray-300/50 shadow-lg md:shadow-xl shadow-gray-300/50 overflow-hidden">
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-dark to-secondary opacity-60 z-20"></div>
        
        <div className="overflow-hidden" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          <div className="overflow-x-auto overflow-y-auto scrollbar-thin" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50/95 via-white/95 to-white/95 border-b-2 border-gray-200/60 sticky top-0 z-10 backdrop-blur-sm">
                <tr>
                  <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider hidden md:table-cell">
                    Category
                  </th>
                  <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Cost Price
                  </th>
                  <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Selling Price
                  </th>
                  <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-right text-[10px] md:text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product, index) => (
                  <tr 
                    key={product.id}
                    className="hover:bg-gradient-to-r hover:from-primary/5 hover:via-secondary/5 hover:to-primary/5 transition-all duration-200 group"
                  >
                    <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg md:rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative h-10 w-10 md:h-12 md:w-12 lg:h-14 lg:w-14 rounded-lg md:rounded-xl overflow-hidden border-2 border-gray-300 shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300">
                            {product.image || product.images?.[0] ? (
                              <img
                                className="h-full w-full object-cover"
                                src={product.image || product.images?.[0]}
                                alt={product.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const parent = e.target.parentElement;
                                  const fallback = parent.querySelector('.image-fallback');
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className="image-fallback h-full w-full bg-gradient-to-br from-primary/40 via-secondary/40 to-primary/60 flex items-center justify-center text-white font-bold text-xl shadow-inner"
                              style={{ display: (product.image || product.images?.[0]) ? 'none' : 'flex' }}
                            >
                              {product.name.charAt(0).toUpperCase()}
                            </div>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs md:text-sm font-bold text-gray-900 truncate group-hover:text-primary transition-colors">{product.name}</div>
                          {product.description && (
                            <div className="text-[10px] md:text-xs text-gray-500 truncate mt-0.5 hidden md:block">{product.description.substring(0, 50)}...</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap hidden md:table-cell">
                      <span className="text-[10px] md:text-xs font-semibold text-gray-600 bg-gray-100 px-2 md:px-2.5 py-0.5 md:py-1 rounded-md md:rounded-lg">
                        {product.category?.name || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap">
                      <span className="text-xs md:text-sm font-black text-gray-700">
                        {formatCurrency(product.cost_price || product.original_price || 0)}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap">
                      <div>
                        <span className="text-xs md:text-sm font-black text-gray-900">
                          {formatCurrency(product.price || 0)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 md:gap-2">
                        {(() => {
                          const totalStock = calculateTotalStock(product);
                          return (
                            <>
                              <span className={`text-xs md:text-sm font-bold ${
                                totalStock > 10 ? 'text-emerald-700' : 
                                totalStock > 0 ? 'text-orange-700' : 'text-red-700'
                              }`}>
                                {totalStock}
                              </span>
                              <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${
                                totalStock > 10 ? 'bg-emerald-500' : 
                                totalStock > 0 ? 'bg-orange-500' : 'bg-red-500'
                              }`}></div>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleProductStatus(product)}
                        disabled={submitLoading}
                        className={`px-2 md:px-3 py-1 md:py-1.5 inline-flex text-[10px] md:text-xs font-bold rounded-md md:rounded-lg shadow-sm cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
                          product.is_active !== false
                            ? 'bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-700 border-2 border-emerald-200/50 hover:from-emerald-200 hover:to-emerald-100'
                            : 'bg-gradient-to-r from-red-100 to-red-50 text-red-700 border-2 border-red-200/50 hover:from-red-200 hover:to-red-100'
                        }`}
                        title={`Click to ${product.is_active !== false ? 'deactivate' : 'activate'} product`}
                      >
                        {product.is_active !== false ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1 md:gap-1.5 lg:gap-2">
                        {/* View Button */}
                        <div className="relative group/tooltip">
                          <button 
                            onClick={() => handleViewProduct(product.id)}
                            className="p-1.5 md:p-2 lg:p-2.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md md:rounded-lg transition-all duration-200 border-2 border-transparent hover:border-blue-200/50 group/btn shadow-sm hover:shadow-md"
                            title="View Product"
                          >
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {/* Tooltip */}
                          <div className="absolute right-0 bottom-full mb-2 px-2 py-1 text-[10px] md:text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            View Product
                            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>

                        {/* Edit Button */}
                        <div className="relative group/tooltip">
                          <button 
                            onClick={() => handleEditProduct(product.id)}
                            className="p-1.5 md:p-2 lg:p-2.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md md:rounded-lg transition-all duration-200 border-2 border-transparent hover:border-emerald-200/50 group/btn shadow-sm hover:shadow-md"
                            title="Edit Product"
                          >
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {/* Tooltip */}
                          <div className="absolute right-0 bottom-full mb-2 px-2 py-1 text-[10px] md:text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Edit Product
                            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>

                        {/* Delete Button */}
                        <div className="relative group/tooltip">
                          <button 
                            onClick={() => openDeleteModal(product)}
                            className="p-1.5 md:p-2 lg:p-2.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md md:rounded-lg transition-all duration-200 border-2 border-transparent hover:border-red-200/50 group/btn shadow-sm hover:shadow-md"
                            title="Delete Product"
                          >
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          {/* Tooltip */}
                          <div className="absolute right-0 bottom-full mb-2 px-2 py-1 text-[10px] md:text-xs font-medium text-white bg-gray-900 rounded-md opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Delete Product
                            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-3 md:px-6 py-8 md:py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-gray-500">No products found</p>
                      {searchQuery && <p className="text-xs text-gray-400 mt-1">Try adjusting your search or filters</p>}
                    </div>
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Results Count */}
      {filteredProducts.length > 0 && (
        <div className="mt-4 text-sm text-gray-600 font-medium">
          Showing <span className="font-bold text-gray-900">{filteredProducts.length}</span> of <span className="font-bold text-gray-900">{products.length}</span> products
        </div>
      )}

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddProduct}
        loading={submitLoading}
        isEditMode={false}
      />

      {/* View Product Modal */}
      <ViewProductModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />

      {/* Edit Product Modal */}
      <AddProductModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProduct(null);
        }}
        onSubmit={handleUpdateProduct}
        loading={submitLoading}
        product={selectedProduct}
        isEditMode={true}
      />

      {/* Delete Product Modal */}
      <DeleteProductModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onConfirm={handleDeleteProduct}
        loading={submitLoading}
      />
    </div>
  );
}

export default Products;
